const CONTENT_DISPOSITION_FILENAME_STAR = /filename\*=UTF-8''"?([^";]+)"?/i
const CONTENT_DISPOSITION_FILENAME = /filename="?([^";]+)"?/i

const parseAttachmentFilename = (contentDisposition: string | null): string | undefined => {
  const filename =
    contentDisposition?.match(CONTENT_DISPOSITION_FILENAME_STAR)?.[1] ||
    contentDisposition?.match(CONTENT_DISPOSITION_FILENAME)?.[1]
  if (!filename) {
    return undefined
  }

  try {
    return decodeURIComponent(filename)
  } catch {
    return filename
  }
}

const parseUrlFilename = (url: string): string | undefined => {
  try {
    return new URL(url).pathname.split('/').pop() || undefined
  } catch {
    return undefined
  }
}

/**
 * Downloads a url as a file without leaving the page. The response is buffered
 * into a blob and handed to a throwaway anchor, so the browser saves it straight
 * away instead of flashing a tab the way a plain navigation does. The name comes
 * from the response's `content-disposition`, falling back to the url path.
 */
export const downloadFileFromUrl = async (url: string, fallbackFilename?: string) => {
  const response = await fetch(url, { credentials: 'omit' })
  if (!response.ok) {
    throw new Error(`Failed to download ${url}: ${response.status} ${response.statusText}`)
  }

  const blob = await response.blob()
  const objectUrl = URL.createObjectURL(blob)

  try {
    const link = document.createElement('a')
    link.href = objectUrl
    link.rel = 'noopener'
    link.download =
      parseAttachmentFilename(response.headers.get('content-disposition')) ||
      parseUrlFilename(url) ||
      fallbackFilename ||
      'download'
    document.body.appendChild(link)
    link.click()
    link.remove()
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}
